// WalnutFix CRM — seed data (Allen, TX appliance repair shop)
// Mock data for the demo. Ported verbatim from the Claude Design bundle.

export type ClientKind = 'business' | 'rental' | 'homeowner' | 'insurance';

export interface Address {
  label: string;
  line1: string;
  line2?: string;
  city: string;
  state: string;
  zip: string;
}

export interface Client {
  id: string;
  name: string;
  kind: ClientKind;
  phone: string;
  email: string | null;
  tags: string[];
  addresses: Address[];
  parentId: string | null;
  notes: string;
  createdAt: string;
}

export interface Tech {
  id: string;
  name: string;
  initials: string;
  color: string;
}

export type JobStatus =
  | 'New'
  | 'Scheduled'
  | 'In Progress'
  | 'Waiting Parts'
  | 'Completed'
  | 'Follow-up'
  | 'Cancelled';

export interface Appliance {
  type: string;
  brand: string;
  model: string;
  serial: string;
}

export interface Job {
  id: string;
  clientId: string;
  addressIdx: number;
  status: JobStatus;
  appliance: Appliance;
  problem: string;
  source: string;
  sourceCompany: string | null;
  techId: string;
  scheduledStart: string;
  scheduledEnd: string;
  photos: number;
  notesCount: number;
}

export interface JobNote {
  author: string;
  authorId: string;
  timestamp: string;
  body: string;
}

export type InvoiceStatus = 'Paid' | 'Sent' | 'Overdue' | 'Draft';

export interface Invoice {
  id: string;
  jobId: string;
  clientId: string;
  issued: string;
  due: string;
  status: InvoiceStatus;
  subtotal: number;
  tax: number;
  total: number;
  paid: number;
}

export type EstimateStatus = 'Draft' | 'Sent' | 'Approved' | 'Declined' | 'Converted';

export interface Estimate {
  id: string;
  jobId: string;
  clientId: string;
  issued: string;
  status: EstimateStatus;
  total: number;
}

export interface Part {
  id: string;
  name: string;
  sku: string;
  stock: number;
  threshold: number;
  vendor: string;
}

export const STATUS_FLOW: JobStatus[] = [
  'New',
  'Scheduled',
  'In Progress',
  'Waiting Parts',
  'Completed',
  'Follow-up',
];

// Pinned demo "now" so the data is deterministic on every load.
export const NOW = new Date('2026-04-30T09:00:00').toISOString();

function dt(daysOffset: number, hour = 9, minute = 0): string {
  const d = new Date(NOW);
  d.setDate(d.getDate() + daysOffset);
  d.setHours(hour, minute, 0, 0);
  return d.toISOString();
}

export const clients: Client[] = [
  {
    id: 'CL-001',
    name: 'Cottonwood Property Management',
    kind: 'business',
    phone: '(972) 555-0142',
    email: 'maintenance@cottonwoodpm.com',
    tags: ['Property Mgmt', 'Net 30', 'High Volume'],
    addresses: [
      {
        label: 'Office',
        line1: '210 W McDermott Dr',
        line2: 'Suite 400',
        city: 'Allen',
        state: 'TX',
        zip: '75013',
      },
    ],
    parentId: null,
    notes:
      'Manages 60+ rental units across Allen and Plano. Always send invoice to office, never tenant. Net 30 payment terms.',
    createdAt: '2024-08-12',
  },
  {
    id: 'CL-002',
    name: 'Tenant — 1418 Aspen Ln (Allen)',
    kind: 'rental',
    phone: '(214) 555-0188',
    email: null,
    tags: ['Tenant', 'Rental'],
    addresses: [
      { label: 'Property', line1: '1418 Aspen Lane', city: 'Allen', state: 'TX', zip: '75002' },
    ],
    parentId: 'CL-001',
    notes: 'Bill parent (Cottonwood). Tenant Sarah Whitfield, prefers texts.',
    createdAt: '2025-03-04',
  },
  {
    id: 'CL-003',
    name: 'Tenant — 822 Bethany Dr (Plano)',
    kind: 'rental',
    phone: '(469) 555-0271',
    email: null,
    tags: ['Tenant', 'Rental'],
    addresses: [
      { label: 'Property', line1: '822 Bethany Drive', city: 'Plano', state: 'TX', zip: '75074' },
    ],
    parentId: 'CL-001',
    notes: 'Bill parent (Cottonwood). Tenant Marcus Lee.',
    createdAt: '2025-09-22',
  },
  {
    id: 'CL-004',
    name: 'Diane Rodríguez',
    kind: 'homeowner',
    phone: '(972) 555-0319',
    email: 'diane.r@gmail.com',
    tags: ['Repeat'],
    addresses: [
      { label: 'Home', line1: '604 Glenwood Drive', city: 'Allen', state: 'TX', zip: '75002' },
    ],
    parentId: null,
    notes: 'Older washer (15 yrs). Has called twice this year.',
    createdAt: '2024-06-19',
  },
  {
    id: 'CL-005',
    name: 'Brett & Hannah Yamamoto',
    kind: 'homeowner',
    phone: '(214) 555-0407',
    email: 'brett.y@outlook.com',
    tags: ['Allen Heights HOA'],
    addresses: [
      { label: 'Home', line1: '1209 Oakhaven Ct', city: 'Allen', state: 'TX', zip: '75013' },
    ],
    parentId: null,
    notes: 'Gate code 4421. Dog (friendly).',
    createdAt: '2025-01-30',
  },
  {
    id: 'CL-006',
    name: 'Mike Johansson',
    kind: 'homeowner',
    phone: '(972) 555-0552',
    email: 'mikej.fairview@gmail.com',
    tags: [],
    addresses: [
      { label: 'Home', line1: '1077 Fairview Pkwy', city: 'Fairview', state: 'TX', zip: '75069' },
    ],
    parentId: null,
    notes: '',
    createdAt: '2025-11-08',
  },
  {
    id: 'CL-007',
    name: 'StateFarm Claims (P. Nguyen)',
    kind: 'insurance',
    phone: '(800) 555-0166',
    email: 'claims-tx-12@partner.example',
    tags: ['Insurance', 'Net 45'],
    addresses: [
      {
        label: 'Claim Site — current',
        line1: '730 Cedar Hollow Ln',
        city: 'McKinney',
        state: 'TX',
        zip: '75070',
      },
    ],
    parentId: null,
    notes:
      'Adjuster Patrick Nguyen. Always attach photos to invoice. Claim # required on every invoice.',
    createdAt: '2025-12-02',
  },
  {
    id: 'CL-008',
    name: 'Lupita Hernández',
    kind: 'homeowner',
    phone: '(469) 555-0731',
    email: 'lupita.h.tx@gmail.com',
    tags: ['Spanish preferred'],
    addresses: [
      {
        label: 'Home',
        line1: '88 Stacy Rd',
        line2: 'Apt 14B',
        city: 'Allen',
        state: 'TX',
        zip: '75002',
      },
    ],
    parentId: null,
    notes: 'Prefers Spanish-speaking tech if possible.',
    createdAt: '2025-04-17',
  },
  {
    id: 'CL-009',
    name: 'Greenway Apartments',
    kind: 'business',
    phone: '(972) 555-0844',
    email: 'office@greenwayapt.example',
    tags: ['Property Mgmt'],
    addresses: [
      {
        label: 'Leasing Office',
        line1: '500 Greenway Blvd',
        city: 'Allen',
        state: 'TX',
        zip: '75013',
      },
    ],
    parentId: null,
    notes: '32-unit complex. Coordinate access with leasing office.',
    createdAt: '2024-11-04',
  },
  {
    id: 'CL-010',
    name: 'Tom & Susan Whitaker',
    kind: 'homeowner',
    phone: '(214) 555-0915',
    email: 'whitaker.tx@gmail.com',
    tags: ['VIP'],
    addresses: [
      {
        label: 'Home',
        line1: '2410 Watters Creek Blvd',
        city: 'Allen',
        state: 'TX',
        zip: '75013',
      },
    ],
    parentId: null,
    notes: 'Premium clients — always confirm time the day before.',
    createdAt: '2024-02-22',
  },
];

export const techs: Tech[] = [
  { id: 'T1', name: 'Carlos Reyes', initials: 'CR', color: 'oklch(0.65 0.13 30)' },
  { id: 'T2', name: 'Jamal Foster', initials: 'JF', color: 'oklch(0.55 0.13 250)' },
  { id: 'T3', name: 'Ana Martínez', initials: 'AM', color: 'oklch(0.6 0.13 150)' },
  { id: 'T4', name: 'Devin Park', initials: 'DP', color: 'oklch(0.6 0.13 300)' },
];

export const jobs: Job[] = [
  // Today
  {
    id: 'JOB-2026-0042',
    clientId: 'CL-002',
    addressIdx: 0,
    status: 'In Progress',
    appliance: { type: 'Washer', brand: 'Whirlpool', model: 'WTW5000DW', serial: 'C72840921' },
    problem: 'Washer not spinning. Drum makes loud noise during agitation.',
    source: 'Property Mgmt — Cottonwood',
    sourceCompany: 'Cottonwood Property Management',
    techId: 'T1',
    scheduledStart: dt(0, 9, 0),
    scheduledEnd: dt(0, 11, 0),
    photos: 3,
    notesCount: 4,
  },
  {
    id: 'JOB-2026-0043',
    clientId: 'CL-005',
    addressIdx: 0,
    status: 'Scheduled',
    appliance: { type: 'Refrigerator', brand: 'LG', model: 'LRMVS3006S', serial: 'KR5102TX' },
    problem: 'Ice maker not dispensing. Water filter recently replaced.',
    source: 'Direct call',
    sourceCompany: null,
    techId: 'T2',
    scheduledStart: dt(0, 11, 30),
    scheduledEnd: dt(0, 13, 0),
    photos: 1,
    notesCount: 1,
  },
  {
    id: 'JOB-2026-0044',
    clientId: 'CL-008',
    addressIdx: 0,
    status: 'Scheduled',
    appliance: { type: 'Dishwasher', brand: 'Bosch', model: 'SHE3AR75UC', serial: '' },
    problem: 'Dishwasher leaks during cycle. Water on kitchen floor.',
    source: 'Direct call',
    sourceCompany: null,
    techId: 'T1',
    scheduledStart: dt(0, 13, 30),
    scheduledEnd: dt(0, 15, 0),
    photos: 0,
    notesCount: 0,
  },
  {
    id: 'JOB-2026-0045',
    clientId: 'CL-006',
    addressIdx: 0,
    status: 'New',
    appliance: { type: 'Dryer', brand: 'Samsung', model: 'DVE45T6000W', serial: '' },
    problem: 'Dryer runs but no heat. Clothes coming out wet.',
    source: 'Web form',
    sourceCompany: null,
    techId: 'T3',
    scheduledStart: dt(0, 15, 30),
    scheduledEnd: dt(0, 17, 0),
    photos: 0,
    notesCount: 0,
  },
  // Tomorrow
  {
    id: 'JOB-2026-0046',
    clientId: 'CL-003',
    addressIdx: 0,
    status: 'Scheduled',
    appliance: { type: 'Oven', brand: 'GE', model: 'JB655SKSS', serial: '' },
    problem: 'Oven not reaching set temperature. Bake element may be out.',
    source: 'Property Mgmt — Cottonwood',
    sourceCompany: 'Cottonwood Property Management',
    techId: 'T2',
    scheduledStart: dt(1, 9, 0),
    scheduledEnd: dt(1, 10, 30),
    photos: 0,
    notesCount: 1,
  },
  {
    id: 'JOB-2026-0047',
    clientId: 'CL-010',
    addressIdx: 0,
    status: 'Scheduled',
    appliance: { type: 'Refrigerator', brand: 'Sub-Zero', model: 'BI-36U/S', serial: 'SZ208310' },
    problem: 'Compressor cycling — fridge running warm.',
    source: 'Repeat customer',
    sourceCompany: null,
    techId: 'T1',
    scheduledStart: dt(1, 10, 30),
    scheduledEnd: dt(1, 12, 30),
    photos: 0,
    notesCount: 0,
  },
  {
    id: 'JOB-2026-0048',
    clientId: 'CL-007',
    addressIdx: 0,
    status: 'Scheduled',
    appliance: { type: 'Washer', brand: 'Maytag', model: 'MVWX655DW', serial: '' },
    problem: 'Insurance claim — flood damage assessment & repair quote.',
    source: 'Insurance — StateFarm',
    sourceCompany: 'StateFarm Claims (Claim # SF-TX-22918)',
    techId: 'T3',
    scheduledStart: dt(1, 13, 0),
    scheduledEnd: dt(1, 14, 30),
    photos: 0,
    notesCount: 0,
  },
  // Day after
  {
    id: 'JOB-2026-0049',
    clientId: 'CL-009',
    addressIdx: 0,
    status: 'Scheduled',
    appliance: { type: 'Dryer', brand: 'Speed Queen', model: 'DR7000WG', serial: '' },
    problem: 'Apt #214 — gas dryer not igniting.',
    source: 'Property Mgmt — Greenway',
    sourceCompany: 'Greenway Apartments',
    techId: 'T4',
    scheduledStart: dt(2, 9, 30),
    scheduledEnd: dt(2, 11, 0),
    photos: 0,
    notesCount: 0,
  },
  {
    id: 'JOB-2026-0050',
    clientId: 'CL-004',
    addressIdx: 0,
    status: 'Scheduled',
    appliance: { type: 'Washer', brand: 'Whirlpool', model: 'WTW4955HW', serial: '' },
    problem: 'Repeat customer — same washer agitator issue from January.',
    source: 'Direct call',
    sourceCompany: null,
    techId: 'T1',
    scheduledStart: dt(2, 13, 0),
    scheduledEnd: dt(2, 14, 30),
    photos: 0,
    notesCount: 0,
  },
  // This week (later)
  {
    id: 'JOB-2026-0051',
    clientId: 'CL-005',
    addressIdx: 0,
    status: 'Scheduled',
    appliance: { type: 'Microwave', brand: 'GE Profile', model: 'PVM9005SJSS', serial: '' },
    problem: 'Microwave keypad unresponsive on top row.',
    source: 'Direct call',
    sourceCompany: null,
    techId: 'T2',
    scheduledStart: dt(3, 10, 0),
    scheduledEnd: dt(3, 11, 0),
    photos: 0,
    notesCount: 0,
  },
  {
    id: 'JOB-2026-0052',
    clientId: 'CL-009',
    addressIdx: 0,
    status: 'Scheduled',
    appliance: { type: 'Refrigerator', brand: 'Frigidaire', model: 'FFTR1814TW', serial: '' },
    problem: 'Apt #108 — fridge not cooling.',
    source: 'Property Mgmt — Greenway',
    sourceCompany: 'Greenway Apartments',
    techId: 'T3',
    scheduledStart: dt(3, 14, 0),
    scheduledEnd: dt(3, 15, 30),
    photos: 0,
    notesCount: 0,
  },
  // Waiting on parts
  {
    id: 'JOB-2026-0040',
    clientId: 'CL-010',
    addressIdx: 0,
    status: 'Waiting Parts',
    appliance: { type: 'Dishwasher', brand: 'Miele', model: 'G7106SCUSS', serial: 'M883914' },
    problem: 'Pump motor needs replacement. Part on order.',
    source: 'Direct call',
    sourceCompany: null,
    techId: 'T1',
    scheduledStart: dt(-3, 10, 0),
    scheduledEnd: dt(-3, 12, 0),
    photos: 2,
    notesCount: 3,
  },
  {
    id: 'JOB-2026-0041',
    clientId: 'CL-001',
    addressIdx: 0,
    status: 'Waiting Parts',
    appliance: { type: 'Refrigerator', brand: 'Whirlpool', model: 'WRS588FIHZ', serial: '' },
    problem: 'Compressor failure — replacement compressor on backorder, ETA Friday.',
    source: 'Property Mgmt — Cottonwood',
    sourceCompany: 'Cottonwood Property Management',
    techId: 'T2',
    scheduledStart: dt(-5, 13, 0),
    scheduledEnd: dt(-5, 15, 0),
    photos: 5,
    notesCount: 6,
  },
  // Completed (history)
  {
    id: 'JOB-2026-0035',
    clientId: 'CL-002',
    addressIdx: 0,
    status: 'Completed',
    appliance: { type: 'Garbage Disposal', brand: 'InSinkErator', model: 'Badger 5', serial: '' },
    problem: 'Disposal jammed.',
    source: 'Property Mgmt — Cottonwood',
    sourceCompany: 'Cottonwood Property Management',
    techId: 'T3',
    scheduledStart: dt(-7, 14, 0),
    scheduledEnd: dt(-7, 15, 0),
    photos: 1,
    notesCount: 2,
  },
  {
    id: 'JOB-2026-0033',
    clientId: 'CL-005',
    addressIdx: 0,
    status: 'Completed',
    appliance: { type: 'Oven', brand: 'LG', model: 'LRG3193ST', serial: '' },
    problem: 'Bake element burned out — replaced.',
    source: 'Direct call',
    sourceCompany: null,
    techId: 'T1',
    scheduledStart: dt(-9, 9, 0),
    scheduledEnd: dt(-9, 10, 30),
    photos: 2,
    notesCount: 1,
  },
  {
    id: 'JOB-2026-0030',
    clientId: 'CL-004',
    addressIdx: 0,
    status: 'Completed',
    appliance: { type: 'Washer', brand: 'Whirlpool', model: 'WTW5000DW', serial: '' },
    problem: 'Drain pump replaced.',
    source: 'Direct call',
    sourceCompany: null,
    techId: 'T1',
    scheduledStart: dt(-14, 11, 0),
    scheduledEnd: dt(-14, 12, 30),
    photos: 1,
    notesCount: 1,
  },
  {
    id: 'JOB-2026-0028',
    clientId: 'CL-008',
    addressIdx: 0,
    status: 'Completed',
    appliance: { type: 'Dryer', brand: 'Samsung', model: 'DVE45T6000W', serial: '' },
    problem: 'Heating element replacement.',
    source: 'Direct call',
    sourceCompany: null,
    techId: 'T2',
    scheduledStart: dt(-18, 10, 0),
    scheduledEnd: dt(-18, 11, 30),
    photos: 1,
    notesCount: 1,
  },
  // Follow-up
  {
    id: 'JOB-2026-0026',
    clientId: 'CL-010',
    addressIdx: 0,
    status: 'Follow-up',
    appliance: { type: 'Refrigerator', brand: 'Sub-Zero', model: 'BI-36U/S', serial: 'SZ208310' },
    problem: 'Re-check temp 2 weeks after compressor service.',
    source: 'Repeat customer',
    sourceCompany: null,
    techId: 'T1',
    scheduledStart: dt(-21, 14, 0),
    scheduledEnd: dt(-21, 15, 0),
    photos: 0,
    notesCount: 1,
  },
  // Cancelled
  {
    id: 'JOB-2026-0024',
    clientId: 'CL-006',
    addressIdx: 0,
    status: 'Cancelled',
    appliance: { type: 'Washer', brand: 'GE', model: '', serial: '' },
    problem: 'Customer cancelled — replaced unit instead.',
    source: 'Direct call',
    sourceCompany: null,
    techId: 'T3',
    scheduledStart: dt(-25, 13, 0),
    scheduledEnd: dt(-25, 14, 0),
    photos: 0,
    notesCount: 1,
  },
];

export const jobNotes: Record<string, JobNote[]> = {
  'JOB-2026-0042': [
    {
      author: 'Carlos R.',
      authorId: 'T1',
      timestamp: '2026-04-30T09:14:00',
      body: 'Arrived on site. Tenant let me in.',
    },
    {
      author: 'Carlos R.',
      authorId: 'T1',
      timestamp: '2026-04-30T09:32:00',
      body: 'Drum bearings making grinding noise. Pulled rear panel.',
    },
    {
      author: 'Carlos R.',
      authorId: 'T1',
      timestamp: '2026-04-30T09:48:00',
      body: 'Bearings shot. Will need to order new bearing kit. Out of stock locally — checking with Marcone.',
    },
    {
      author: 'Owner (Lin)',
      authorId: 'OWNER',
      timestamp: '2026-04-30T10:02:00',
      body: 'OK to order — billing to Cottonwood, Net 30.',
    },
  ],
  'JOB-2026-0041': [
    {
      author: 'Jamal F.',
      authorId: 'T2',
      timestamp: '2026-04-25T13:18:00',
      body: 'Compressor fully dead. Quoted $640 part + $180 labor.',
    },
    {
      author: 'Jamal F.',
      authorId: 'T2',
      timestamp: '2026-04-25T13:45:00',
      body: 'Cottonwood approved over phone. Ordering.',
    },
    {
      author: 'Owner (Lin)',
      authorId: 'OWNER',
      timestamp: '2026-04-26T09:00:00',
      body: 'Backordered — ETA Friday May 1. Reschedule install for next Monday.',
    },
  ],
  'JOB-2026-0040': [
    {
      author: 'Carlos R.',
      authorId: 'T1',
      timestamp: '2026-04-27T10:22:00',
      body: 'Pump seized. Manufacturer warranty expired.',
    },
    {
      author: 'Carlos R.',
      authorId: 'T1',
      timestamp: '2026-04-27T11:05:00',
      body: 'Quoted customer $310. Approved.',
    },
  ],
};

export const invoices: Invoice[] = [
  {
    id: 'INV-2026-0118',
    jobId: 'JOB-2026-0035',
    clientId: 'CL-002',
    issued: '2026-04-23',
    due: '2026-05-23',
    status: 'Paid',
    subtotal: 145.0,
    tax: 11.96,
    total: 156.96,
    paid: 156.96,
  },
  {
    id: 'INV-2026-0119',
    jobId: 'JOB-2026-0033',
    clientId: 'CL-005',
    issued: '2026-04-21',
    due: '2026-04-28',
    status: 'Paid',
    subtotal: 285.0,
    tax: 23.51,
    total: 308.51,
    paid: 308.51,
  },
  {
    id: 'INV-2026-0120',
    jobId: 'JOB-2026-0030',
    clientId: 'CL-004',
    issued: '2026-04-16',
    due: '2026-04-23',
    status: 'Overdue',
    subtotal: 220.0,
    tax: 18.15,
    total: 238.15,
    paid: 0,
  },
  {
    id: 'INV-2026-0121',
    jobId: 'JOB-2026-0028',
    clientId: 'CL-008',
    issued: '2026-04-12',
    due: '2026-04-19',
    status: 'Sent',
    subtotal: 195.0,
    tax: 16.09,
    total: 211.09,
    paid: 0,
  },
  {
    id: 'INV-2026-0117',
    jobId: 'JOB-2026-0026',
    clientId: 'CL-010',
    issued: '2026-04-09',
    due: '2026-04-16',
    status: 'Paid',
    subtotal: 95.0,
    tax: 7.84,
    total: 102.84,
    paid: 102.84,
  },
];

export const estimates: Estimate[] = [
  {
    id: 'EST-2026-0061',
    jobId: 'JOB-2026-0048',
    clientId: 'CL-007',
    issued: '2026-04-29',
    status: 'Sent',
    total: 540.0,
  },
  {
    id: 'EST-2026-0060',
    jobId: 'JOB-2026-0040',
    clientId: 'CL-010',
    issued: '2026-04-27',
    status: 'Approved',
    total: 310.5,
  },
  {
    id: 'EST-2026-0059',
    jobId: 'JOB-2026-0041',
    clientId: 'CL-001',
    issued: '2026-04-25',
    status: 'Converted',
    total: 887.42,
  },
];

export const parts: Part[] = [
  { id: 'P-001', name: 'Whirlpool Washer Bearing Kit', sku: 'W10772617', stock: 0, threshold: 2, vendor: 'Marcone' },
  { id: 'P-002', name: 'Samsung Dryer Heating Element', sku: 'DC47-00019A', stock: 3, threshold: 2, vendor: 'Marcone' },
  { id: 'P-003', name: 'GE Range Bake Element', sku: 'WB44T10010', stock: 5, threshold: 3, vendor: 'Reliable Parts' },
  { id: 'P-004', name: 'LG Refrigerator Water Filter', sku: 'LT1000P', stock: 8, threshold: 4, vendor: 'Reliable Parts' },
  { id: 'P-005', name: 'Bosch Dishwasher Drain Pump', sku: '00611332', stock: 1, threshold: 2, vendor: 'Marcone' },
  { id: 'P-006', name: 'Whirlpool Drain Pump', sku: 'W10730972', stock: 4, threshold: 3, vendor: 'Marcone' },
  { id: 'P-007', name: 'Universal Refrigerator Compressor (1/3 HP)', sku: 'COMP-13H', stock: 0, threshold: 1, vendor: 'AppliancePartsPros' },
  { id: 'P-008', name: 'Maytag Washer Lid Lock', sku: 'W10810403', stock: 2, threshold: 2, vendor: 'Marcone' },
  { id: 'P-009', name: 'Frigidaire Ice Maker Module', sku: '241798231', stock: 3, threshold: 2, vendor: 'Reliable Parts' },
  { id: 'P-010', name: 'GE Microwave Membrane Switch', sku: 'WB27X11086', stock: 2, threshold: 2, vendor: 'AppliancePartsPros' },
  { id: 'P-011', name: 'Universal Dryer Belt 4-rib', sku: 'DRYBELT-4', stock: 12, threshold: 5, vendor: 'Marcone' },
  { id: 'P-012', name: 'Sub-Zero Door Gasket (36")', sku: 'SZ-7028361', stock: 1, threshold: 1, vendor: 'AppliancePartsPros' },
  { id: 'P-013', name: 'InSinkErator Mount Gasket', sku: 'ISE-MGSKT', stock: 6, threshold: 4, vendor: 'Reliable Parts' },
  { id: 'P-014', name: 'Speed Queen Gas Valve', sku: 'SQ-330209', stock: 1, threshold: 2, vendor: 'Marcone' },
  { id: 'P-015', name: 'Miele Dishwasher Pump Motor', sku: 'M-7253480', stock: 0, threshold: 1, vendor: 'AppliancePartsPros' },
];
